import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TableCell,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";

import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import DeleteModal from "components/UI/Form/partials/Delete";
import AddModal from "components/UI/Form/partials/Add";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import EditModal from "components/UI/Form/partials/Edit";
import { getPlans } from "framework/plans";
import { IPlan } from "models/plan";
import { ISubscription, ISubscriptionInput } from "models/subscription";
import { addSubscription, deleteSubscription, editSubscription, getSubscription, getSubscriptionStatus, paySubscription, SendReminder,SendSupportMsg, SubscriptionCells } from "framework/subscription";
import { activityClient, getClients } from "framework/clients";
import { IClient } from "models/clients";
import { getServices } from "framework/service";
import { IService } from "models/services";
import FiltersDate from "./partials/FiltersDate";
import { useLocation } from "react-router-dom";
import ActivityModal from "components/UI/Form/partials/Activity";
import { getReseller } from "framework/reseller";
import { IReseller } from "models/reseller";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PayInAdvanceModal from "./partials/PayInAdvance";
import AddSubscriptionModal from "./partials/Add";
import EditSubscriptionModal from "./partials/Edit";
import MuiButton from "components/UI/MuiButton";

interface Props {
  feature: Feature;
}

const Subscription: React.FC<Props> = ({ feature }) => {
  const params = new URLSearchParams(window.location.search);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [FilterSubscription, setFilterSubscription] = useState<ISubscription[]>([] as ISubscription[]);
  const [plans, setPlans] = useState<IPlan[]>([] as IPlan[]);
  const [clients, setClients] = useState<IClient[]>([] as IClient[]);
  const [resellers, setResellers] = useState<IReseller[]>([] as IReseller[]);
  const [services, setServices] = useState<IService[]>([] as IService[]);
  const [status, setStatus] = useState<string[]>([] as string[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openPay, setOpenPay] = useState<boolean>(false);
  const [openPayAdvanced, setOpenPayAdvanced] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<ISubscription | null>(null);
  const [inputObj, setInputObj] = useState<ISubscriptionInput>({} as ISubscriptionInput);
  const [statusFilter, setStatusFilter] = useState<string>(params.get('status') || '');
  const [planFilter, setPlanFilter] = useState<string>(params.get('plan') || '');
  const [serviceFilter, setServiceFilter] = useState<string>(params.get('serviceId') || '');
  const [resellerFilter, setResellerFilter] = useState<string>(params.get('resellerId') || '');
  const [dateFrom, setDateFrom] = useState<any>(params.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState<any>(params.get('dateTo') || '');


  const updateQueryParams = () => {
    const params = new URLSearchParams(window.location.search);

    statusFilter ? params.set('status', statusFilter) : params.delete('status');
    planFilter ? params.set('plan', planFilter) : params.delete('plan');
    serviceFilter ? params.set('serviceId', serviceFilter) : params.delete('serviceId');
    resellerFilter ? params.set('resellerId', resellerFilter) : params.delete('resellerId');
    dateFrom ? params.set('dateFrom', new Intl.DateTimeFormat("en-LB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(dateFrom).replace(/\//g, "-")) : params.delete('dateFrom');
    dateTo ? params.set('dateTo', new Intl.DateTimeFormat("en-LB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(dateTo).replace(/\//g, "-")) : params.delete('dateTo');

    window.history.replaceState(null, '', '?' + params.toString());
    revalidate()
  };


  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addMutation = useMutation({
    mutationFn: (createInput: ISubscriptionInput) => {
      return addSubscription(createInput);
    },
  });

  const editMutation = useMutation({
    mutationFn: (data: { createInput: ISubscriptionInput; subscriptionID: string }) => {
      return editSubscription(data.createInput, data.subscriptionID);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteSubscription(id);
    },
  });


  const paySubscriptionMutation = useMutation({
    mutationFn: ({ id, planId }: { id: number, planId?: string }) => {
      return paySubscription(id, planId!);
    },
  });

  const SendSupportMsgMutation = useMutation({
    mutationFn: () => {
      return SendSupportMsg();
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: () => {
      return SendReminder();
    },
  });


  const fetchData = useCallback(async () => {
    try {
      setLoadingFilters(true)
      const [plansResponse, clientsResponse, servicesResponse, resellersResponse, statusResponse] = await Promise.all([
        queryClient.fetchQuery("get plans", () => getPlans()),
        queryClient.fetchQuery("get clients", () => getClients()),
        queryClient.fetchQuery("get services", () => getServices()),
        queryClient.fetchQuery("get resellers", () => getReseller()),
        queryClient.fetchQuery("get subscription statuses", () => getSubscriptionStatus()),
      ]);

      setPlans(plansResponse);
      setClients(clientsResponse);
      setServices(servicesResponse)
      setResellers(resellersResponse)
      setStatus(statusResponse)
      setLoadingFilters(false)
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error.response.data) || "Something went wrong",
        type: "error",
      });
      setLoadingFilters(false)
    }
    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  useEffect(() => {
    updateQueryParams();
  }, [statusFilter, planFilter, serviceFilter, resellerFilter, dateFrom, dateTo]);


  useEffect(() => {
    fetchData();
  }, []);


  const buildQueryParams = () => {
    const params: Record<string, string> = {};
    if (statusFilter) params['status'] = statusFilter;
    if (planFilter) params['plan'] = planFilter;
    if (serviceFilter) params['serviceId'] = serviceFilter;
    if (resellerFilter) params['resellerId'] = resellerFilter;
    if (dateFrom) params['dateFrom'] = new Intl.DateTimeFormat("en-LB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(dateFrom).replace(/\//g, "-");
    if (dateTo) params['dateTo'] = new Intl.DateTimeFormat("en-LB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(dateTo).replace(/\//g, "-");
    return params;
  };


  const {
    loading,
    data: Subscription,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getSubscription(buildQueryParams()),
    fetchPermission: async () => null,
    section: "Services",
    onSuccess: (data) => setFilterSubscription(data),

  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === 'Plans') {
      const array: { label: string, value: string }[] = value
      const data = array.map(item => +item.value)
      setInputObj((prev) => ({
        ...prev,
        [name]: data,
      }));

      return
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);

  const handleOpenEdit = (Service: ISubscription) => {
    setSelectedSubscription(Service);
    setOpenEdit(true);
  };


  const handleOpenPay = (Service: ISubscription) => {
    setSelectedSubscription(Service);
    setOpenPay(true);
  };

  const handleOpenPayAdvanced = (Service: ISubscription) => {
    setSelectedSubscription(Service);
    setOpenPayAdvanced(true);
  };

  const handleOpenDelete = (Service: ISubscription) => {
    setSelectedSubscription(Service);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpenAdd(false)
    setOpenEdit(false)
    setOpenDelete(false)
    setOpenPay(false)
    setOpenPayAdvanced(false)
    setSelectedSubscription(null);
    setInputObj({} as ISubscriptionInput)
  }

  const getBodyCells = (row: ISubscription) => {
    let cells = Object.keys(row).map((key: string, i: number) => {


      if (key === "clientName")
        return {
          id: "clientName",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{row?.clientName}</Typography>
            </TableCell>
          ),
        };

      if (key === "serviceName")
        return {
          id: "serviceName",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{row?.serviceName}</Typography>
            </TableCell>
          ),
        };

      if (key === "resellerName")
        return {
          id: "resellerName",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{row?.resellerName}</Typography>
            </TableCell>
          ),
        };

      if (key === "planName")
        return {
          id: "planName",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{row?.planName}</Typography>
            </TableCell>
          ),
        };

      if (key === "startDate")
        return {
          id: "startDate",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{new Date(row?.startDate).toDateString()}</Typography>
            </TableCell>
          ),
        };

      if (key === "renewalDate")
        return {
          id: "renewalDate",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{new Date(row?.renewalDate).toDateString()}</Typography>
            </TableCell>
          ),
        };

      if (key === "status")
        return {
          id: "status",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Typography variant="body2">{row?.status}</Typography>
            </TableCell>
          ),
        };

      if (key === "serviceId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i} sx={{ background: row.isHighlighted ? '#6dc46d' : '' }}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
              >

                <Tooltip title={"Edit"}>
                  <IconButton
                    size={"small"}
                    color={"primary"}
                    onClick={() => handleOpenEdit(row)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>

                <Tooltip title={"Delete"}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "red",
                    }}
                    onClick={() => handleOpenDelete(row)}
                  >
                    <Delete />
                  </IconButton>

                </Tooltip>

                {row.status === 'Reminder Sent' && <Tooltip title={"Pay"}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "green",
                    }}
                    onClick={() => handleOpenPay(row)}
                  >
                    <AttachMoneyIcon />
                  </IconButton>
                </Tooltip>}

                {row.status === 'Reminder Sent' && <Tooltip title={"Pay in advance"}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "orange",
                    }}
                    onClick={() => handleOpenPayAdvanced(row)}
                  >
                    <AttachMoneyIcon />
                  </IconButton>

                </Tooltip>}



              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    try {
      setLoading2(true);
      let startDate: any = new Date(inputObj.startDate || new Date());
      startDate = new Intl.DateTimeFormat("en-LB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }).format(startDate).replace(/\//g, "-");
      await addMutation.mutateAsync({ ...inputObj, startDate });
      setAlert({
        open: true,
        message: "Subscription added successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setInputObj({} as ISubscriptionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    if (inputObj.planId === '') {
      setAlert({
        open: true,
        message: "Plan is required",
        type: "error",
      });
      return
    }
    try {
      setLoading2(true);
      let startDate: any = new Date(inputObj.startDate || new Date());
      startDate = new Intl.DateTimeFormat("en-LB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }).format(startDate).replace(/\//g, "-");

      await editMutation.mutateAsync({
        createInput: { ...inputObj, startDate },
        subscriptionID: selectedSubscription!?.subscriptionId.toString()
      });
      setAlert({
        open: true,
        message: "Subscription updated successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setInputObj({} as ISubscriptionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteMutation.mutateAsync(selectedSubscription!.subscriptionId.toString());
      setAlert({
        open: true,
        message: "Subscription deleted successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleActivity = async () => {
    try {
      setLoading2(true);
      await paySubscriptionMutation.mutateAsync({ id: selectedSubscription!.subscriptionId });
      setAlert({
        open: true,
        message: "Subscription Paid successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error.response.data) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleAdvancedPay = async () => {

    if (!inputObj.advancePaymentId)
      return setAlert({
        open: true,
        message: "Plan is required",
        type: "error",
      });

    try {
      setLoading2(true);
      await paySubscriptionMutation.mutateAsync({ id: selectedSubscription!.subscriptionId, planId: inputObj.advancePaymentId });
      setAlert({
        open: true,
        message: "Subscription Paid successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error.response.data) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSendReminder = async () => {
    try {
      setLoading2(true);
      await sendReminderMutation.mutateAsync();
      setAlert({
        open: true,
        message: "Reminders have been sent.",
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };


  const handleSendSupportMsg = async () => {
    try {
      setLoading2(true);
      await SendSupportMsgMutation.mutateAsync();
      setAlert({
        open: true,
        message: "Notifictions have been sent.",
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };
  const handleSearch = async (value: string) => {
    if (!value) return setFilterSubscription(Subscription!);
    let filtered = Subscription!.filter((service) =>
      service.serviceName.toLowerCase().includes(value.toLowerCase()) || service.clientName.toLowerCase().includes(value.toLowerCase()) || service.planName.toLowerCase().includes(value.toLowerCase())
    );
    setFilterSubscription(filtered);
  };

  let clientsOptions = clients.map((client) => { return { value: client.clientId.toString(), label: client.clientName } });
  let servicesOptions = services.map((service) => { return { value: service.serviceId.toString(), label: service.serviceName } });
  let plansOptions = services.find(service => service.serviceId.toString() === (inputObj?.serviceId || selectedSubscription?.serviceId.toString()))?.plans?.map((plan) => { return { value: plan.toString(), label: plans.find(item => item.planId === plan)?.planName || plan.toString() } }) || [];
  let statusOptions = status.map((plan) => { return { value: plan, label: plan } });



  let editBasicFields: IFormField[] = [
    {
      name: 'clientId',
      label: 'Client',
      placeholder: 'Select Client',
      type: InputTypes.SELECT,
      options: clientsOptions,
      internalLabel: true,
      defaultValue: selectedSubscription ? clientsOptions.find(client => client.value === selectedSubscription.clientId.toString()) : '',
      required: true
    },
    {
      name: 'serviceId',
      label: 'Service',
      placeholder: 'Select Service',
      type: InputTypes.SELECT,
      options: servicesOptions,
      internalLabel: true,
      defaultValue: selectedSubscription ? servicesOptions.find(service => service.value === selectedSubscription.serviceId.toString()) : '',
      required: true
    },
    {
      name: 'planId',
      label: 'Plan',
      placeholder: 'Select Plan',
      type: InputTypes.SELECT,
      options: plansOptions,
      internalLabel: true,
      defaultValue: selectedSubscription ? plansOptions!.find(plan => plan.value === selectedSubscription.planId.toString()) : '',
      required: true
    },
    {
      name: 'status',
      label: 'Status',
      placeholder: 'Select Status',
      type: InputTypes.SELECT,
      options: statusOptions,
      hidden: true,
      internalLabel: true,
      defaultValue: selectedSubscription ? statusOptions.find(status => status.value === selectedSubscription.status) : '',
      required: true
    },
    {
      name: 'startDate',
      label: 'Start Date',
      placeholder: 'Select Start Date',
      type: InputTypes.CALENDER,
      internalLabel: true,
      defaultValue: selectedSubscription?.startDate,
      required: true
    },


  ];

  const clearFilters = () => {
    setDateFrom(null!)
    setDateTo(null!)
    setServiceFilter('')
    setPlanFilter('')
    setResellerFilter('')
    setStatusFilter('')
  }

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="h3"
              color={"header.primary"}
            >
              {(feature.name)}
            </Typography>

            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <MuiButton variant="contained"
                sx={{ bgcolor: 'primary.main', color: 'white' }}
                disabled={loading}
                size='large' onClick={handleSendSupportMsg}>
                {'Send Support Message'}
                <CircularProgress
                  size={10}
                  sx={{ mx: 2, display: loading2 ? "" : "none" }}
                />
              </MuiButton>
              <MuiButton variant="contained"
                sx={{ bgcolor: 'primary.main', color: 'white' }}
                disabled={loading}
                size='large' onClick={handleSendReminder}>
                {'Send Reminder'}
                <CircularProgress
                  size={10}
                  sx={{ mx: 2, display: loading2 ? "" : "none" }}
                />
              </MuiButton>

              <Tooltip title={"Add"}>
                <IconButton
                  sx={{
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>


            </div>


          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={"Search"} />
            </Stack>
          </Stack>
        </Grid>

        <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: 10 }}>
          <FormControl variant="outlined" style={{ minWidth: '153px' }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as string)}
              label="Status"
            >
              <MenuItem value="" style={{ fontSize: '18px', fontStyle: 'italic', fontWeight: 'lighter' }}>None</MenuItem>
              {status.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" style={{ minWidth: '153px' }}>
            <InputLabel>Plans</InputLabel>
            <Select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value as string)}
              label="Plans"
            >
              <MenuItem value="" style={{ fontSize: '18px', fontStyle: 'italic', fontWeight: 'lighter' }}>None</MenuItem>
              {plans.map((plan) => (
                <MenuItem key={plan.planId} value={plan.planId}>{plan.planName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" style={{ minWidth: '153px' }}>
            <InputLabel>Services</InputLabel>
            <Select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value as string)}
              label="Services"
            >
              <MenuItem value="" style={{ fontSize: '18px', fontStyle: 'italic', fontWeight: 'lighter' }}>None</MenuItem>
              {services.map((service) => (
                <MenuItem key={service.serviceId} value={service.serviceId}>{service.serviceName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" style={{ minWidth: '153px' }}>
            <InputLabel>Resellers</InputLabel>
            <Select
              value={resellerFilter}
              onChange={(e) => setResellerFilter(e.target.value as string)}
              label="Resellers"
            >
              <MenuItem value="" style={{ fontSize: '18px', fontStyle: 'italic', fontWeight: 'lighter' }}>None</MenuItem>
              {resellers.map((reseller) => (
                <MenuItem key={reseller.resellerId} value={reseller.resellerId}>{reseller.resellerName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" style={{ minWidth: '153px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date From"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue!)}
                renderInput={(params) => (
                  <TextField {...params} error={false} variant="outlined" size="small" style={{ fontSize: '18px', fontStyle: 'italic', fontWeight: 'lighter' }} />
                )}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl variant="outlined" style={{ minWidth: '153px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date To"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue!)}
                renderInput={(params) => (
                  <TextField {...params} error={false} variant="outlined" size="small" style={{ fontSize: '18px', fontStyle: 'italic', fontWeight: 'lighter' }} />
                )}
              />
            </LocalizationProvider>
          </FormControl>


          <Button onClick={clearFilters}>Clear</Button>
        </div>
        {FilterSubscription.length > 0 && (
          <List
            headCells={SubscriptionCells}
            data={FilterSubscription}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterSubscription || FilterSubscription.length === 0) &&
          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={"No Subscription"}
            message={"No Subscriptions Exists Yet"}
          />
        }


      </Stack>

      {openAdd && (
        <AddSubscriptionModal
          open={openAdd}
          loading={loading2}
          inputObj={inputObj}
          clients={clients}
          plans={plans}
          services={services}
          title="Add Subscription"
          disabled={!inputObj.clientId || !inputObj.planId || !inputObj.serviceId}
          onClose={handleClose}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditSubscriptionModal
          open={openEdit}
          loading={loading2}
          clients={clients}
          inputObj={inputObj}
          plans={plans}
          selectedSubscription={selectedSubscription!}
          services={services}
          title="Edit Subscription"
          onClose={handleClose}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}

      {openDelete && (
        <DeleteModal
          open={openDelete}
          loading={loading2}
          title="Delete Subscription"
          description="Are You Sure You Want to delete this Subscription"
          onClose={handleClose}
          onSubmit={handleDelete}
        />
      )}

      {openPay && (
        <ActivityModal
          open={openPay}
          loading={loading2}
          title={`Pay Subscription`}
          description={`Are You Sure You Want to Pay this subscription`}
          onClose={handleClose}
          onSubmit={handleActivity}
        />
      )}


      {openPayAdvanced && (
        <PayInAdvanceModal
          open={openPayAdvanced}
          subscription={selectedSubscription!}
          loading={loading2}
          title={`Pay in advance subscription`}
          description={`Select a plan then confirm`}
          onClose={handleClose}
          onSubmit={handleAdvancedPay}
          onChanges={handleChange}
        />
      )}

    </>
  );
};

export default Subscription;
